import { useEffect, useMemo, useState } from "react";
import { ApiError, api, type ApiKey, type WitnessLogSummary } from "../api/client";
import { Pagination } from "../components/Pagination";
import { receiptDetailPath, witnessLogDetailPath } from "../routes";

const PAGE_SIZE = 25;

type DatePreset = "all" | "today" | "7d" | "30d" | "custom";

type Filters = {
  page: number;
  apiKeyId: number | null;
  preset: DatePreset;
  // Only used when preset === "custom". ISO date strings (YYYY-MM-DD)
  // rather than full timestamps; the backend accepts either.
  customSince: string;
  customUntil: string;
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; entries: WitnessLogSummary[]; total: number }
  | { kind: "error"; status: number | null; code: string; message: string };

function readFiltersFromUrl(): Filters {
  const params = new URLSearchParams(window.location.search);
  const pageRaw = params.get("page");
  let page = 1;
  if (pageRaw) {
    const parsed = Number(pageRaw);
    if (Number.isFinite(parsed) && parsed >= 1) page = Math.floor(parsed);
  }
  const apiKeyRaw = params.get("api_key_id");
  let apiKeyId: number | null = null;
  if (apiKeyRaw && /^\d+$/.test(apiKeyRaw)) {
    const parsed = Number(apiKeyRaw);
    if (Number.isFinite(parsed) && parsed > 0) apiKeyId = parsed;
  }
  const presetRaw = params.get("range");
  const preset: DatePreset =
    presetRaw === "today" || presetRaw === "7d" || presetRaw === "30d" || presetRaw === "custom"
      ? presetRaw
      : "all";
  const customSince = params.get("since") ?? "";
  const customUntil = params.get("until") ?? "";
  return { page, apiKeyId, preset, customSince, customUntil };
}

function writeFiltersToUrl(filters: Filters): void {
  const url = new URL(window.location.href);
  if (filters.page <= 1) url.searchParams.delete("page");
  else url.searchParams.set("page", String(filters.page));
  if (filters.apiKeyId === null) url.searchParams.delete("api_key_id");
  else url.searchParams.set("api_key_id", String(filters.apiKeyId));
  if (filters.preset === "all") url.searchParams.delete("range");
  else url.searchParams.set("range", filters.preset);
  if (filters.preset === "custom" && filters.customSince) {
    url.searchParams.set("since", filters.customSince);
  } else {
    url.searchParams.delete("since");
  }
  if (filters.preset === "custom" && filters.customUntil) {
    url.searchParams.set("until", filters.customUntil);
  } else {
    url.searchParams.delete("until");
  }
  window.history.pushState({}, "", url.toString());
}

function resolveDateRange(filters: Filters): { since?: string; until?: string } {
  // Translate the UI preset into ISO timestamps for the backend. The
  // "today" preset uses local midnight; the relative ones use the
  // user's wall clock minus N days. The backend stores UTC timestamps
  // and Date.toISOString() converts; the wider window is fine for the
  // V1 use case (compliance audit, debugging).
  const now = new Date();
  if (filters.preset === "today") {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { since: start.toISOString() };
  }
  if (filters.preset === "7d") {
    const start = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
    return { since: start.toISOString() };
  }
  if (filters.preset === "30d") {
    const start = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
    return { since: start.toISOString() };
  }
  if (filters.preset === "custom") {
    const out: { since?: string; until?: string } = {};
    if (filters.customSince) {
      const d = new Date(filters.customSince);
      if (!Number.isNaN(d.getTime())) out.since = d.toISOString();
    }
    if (filters.customUntil) {
      // Inclusive of the until date by snapping to end-of-day local.
      const d = new Date(filters.customUntil);
      if (!Number.isNaN(d.getTime())) {
        d.setHours(23, 59, 59, 999);
        out.until = d.toISOString();
      }
    }
    return out;
  }
  return {};
}

export function WitnessLogPage(props: { onOpenDetail: (position: number) => void }) {
  const [filters, setFilters] = useState<Filters>(() => readFiltersFromUrl());
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  // Keep URL <-> state in sync on back/forward.
  useEffect(() => {
    function onPopState() {
      setFilters(readFiltersFromUrl());
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Load the customer's API keys once so the filter dropdown can
  // populate. Includes revoked keys: the witness log retains
  // api_key_id forever (it is audit metadata for past signing
  // activity), so a customer who rotated a key still needs to filter
  // by it. Revoked keys render with a "(revoked)" suffix in the
  // dropdown to distinguish them from active ones. Errors here are
  // non-fatal; the dropdown simply doesn't render. The list view
  // itself does not depend on this data.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await api.listApiKeys({ includeRevoked: true });
        if (!cancelled) setKeys(result.keys);
      } catch {
        // Filter falls back to "all keys"; the list still works.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Refetch on any filter change. The filter object captures every
  // dimension that changes the query, so depending on it whole-object
  // is the right trigger.
  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    const range = resolveDateRange(filters);
    (async () => {
      try {
        const response = await api.listWitnessLog({
          page: filters.page,
          pageSize: PAGE_SIZE,
          apiKeyId: filters.apiKeyId ?? undefined,
          since: range.since,
          until: range.until
        });
        if (cancelled) return;
        setState({ kind: "ready", entries: response.entries, total: response.total });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setState({
            kind: "error",
            status: err.status,
            code: err.code,
            message: err.message
          });
          return;
        }
        const message = err instanceof Error ? err.message : "Could not load the witness log.";
        setState({ kind: "error", status: null, code: "network_error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters.page, filters.apiKeyId, filters.preset, filters.customSince, filters.customUntil]);

  function updateFilters(next: Filters) {
    writeFiltersToUrl(next);
    setFilters(next);
  }

  function changePage(nextPage: number) {
    if (nextPage === filters.page) return;
    updateFilters({ ...filters, page: nextPage });
  }

  // Show the API key filter whenever the customer has had more than
  // one key over their lifetime (active or revoked). With a single
  // active key and no history, the filter is operationally
  // meaningless. The dropdown lists active keys first, then revoked
  // ones with a "(revoked)" suffix.
  const filterKeys = useMemo(() => {
    const active = keys.filter((k) => k.revoked_at === null);
    const revoked = keys.filter((k) => k.revoked_at !== null);
    return [...active, ...revoked];
  }, [keys]);
  const showKeyFilter = filterKeys.length > 1;

  return (
    <div className="panel">
      <div className="section-header">
        <h2 className="section-title">Witness Log</h2>
        {state.kind === "ready" ? (
          <span className="section-meta">
            {state.total === 0 ? "No signing activity" : `${state.total.toLocaleString()} total`}
          </span>
        ) : null}
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Every signing call your API keys made, newest first. Includes direct-mode chains that do not
        produce stored receipts at Sequesign. Click a row for details.
      </p>
      <div className="filter-row">
        <label className="filter-control">
          <span className="filter-label">Range:</span>
          <select
            value={filters.preset}
            onChange={(e) => {
              const preset = e.target.value as DatePreset;
              updateFilters({ ...filters, preset, page: 1 });
            }}
          >
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom</option>
          </select>
        </label>
        {filters.preset === "custom" ? (
          <>
            <label className="filter-control">
              <span className="filter-label">From:</span>
              <input
                type="date"
                value={filters.customSince}
                onChange={(e) =>
                  updateFilters({ ...filters, customSince: e.target.value, page: 1 })
                }
              />
            </label>
            <label className="filter-control">
              <span className="filter-label">To:</span>
              <input
                type="date"
                value={filters.customUntil}
                onChange={(e) =>
                  updateFilters({ ...filters, customUntil: e.target.value, page: 1 })
                }
              />
            </label>
          </>
        ) : null}
        {showKeyFilter ? (
          <label className="filter-control">
            <span className="filter-label">API key:</span>
            <select
              value={filters.apiKeyId === null ? "all" : String(filters.apiKeyId)}
              onChange={(e) => {
                const raw = e.target.value;
                updateFilters({
                  ...filters,
                  apiKeyId: raw === "all" ? null : Number(raw),
                  page: 1
                });
              }}
            >
              <option value="all">All keys</option>
              {filterKeys.map((k) => {
                const suffix = k.revoked_at !== null ? " (revoked)" : "";
                return (
                  <option key={k.id} value={k.id}>
                    {k.label ?? k.key_prefix} ({k.key_class}){suffix}
                  </option>
                );
              })}
            </select>
          </label>
        ) : null}
      </div>
      {renderBody(state, filters.page, props.onOpenDetail, changePage)}
    </div>
  );
}

function renderBody(
  state: LoadState,
  page: number,
  onOpenDetail: (position: number) => void,
  onPageChange: (next: number) => void
) {
  if (state.kind === "loading") {
    return <p className="muted">Loading witness log…</p>;
  }
  if (state.kind === "error") {
    return <div className="error">{state.message || "Could not load the witness log."}</div>;
  }
  if (state.total === 0) {
    return (
      <p className="empty-state">
        No signing activity yet. Entries appear here as soon as your SDK records actions.
      </p>
    );
  }
  if (state.entries.length === 0) {
    return (
      <>
        <p className="empty-state">
          This page is past the end of the list. Use the controls below to navigate to a valid page.
        </p>
        <Pagination
          currentPage={page}
          totalItems={state.total}
          pageSize={PAGE_SIZE}
          onPageChange={onPageChange}
        />
      </>
    );
  }
  return (
    <>
      <table className="data-table witness-log-table">
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col" className="numeric">
              Position
            </th>
            <th scope="col">Chain</th>
            <th scope="col">Entry hash</th>
            <th scope="col">Key</th>
            <th scope="col">Batch</th>
          </tr>
        </thead>
        <tbody>
          {state.entries.map((row) => (
            <WitnessLogRow key={row.position} row={row} onOpen={onOpenDetail} />
          ))}
        </tbody>
      </table>
      <Pagination
        currentPage={page}
        totalItems={state.total}
        pageSize={PAGE_SIZE}
        onPageChange={onPageChange}
      />
    </>
  );
}

function WitnessLogRow(props: { row: WitnessLogSummary; onOpen: (position: number) => void }) {
  const { row, onOpen } = props;
  return (
    <tr
      className="data-row data-row-clickable"
      onClick={() => onOpen(row.position)}
      tabIndex={0}
      role="link"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(row.position);
        }
      }}
    >
      <td title={row.created_at}>{formatTimestamp(row.created_at)}</td>
      <td className="numeric">
        <a
          href={witnessLogDetailPath(row.position)}
          className="receipt-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpen(row.position);
          }}
        >
          {row.position}
        </a>
      </td>
      <td>
        {row.evidence_redacted_at ? (
          // Per-receipt erasure (db/migrations/0023): the receipt + hosted
          // evidence were deleted from the platform, but the chain_id is
          // retained so this reads as "deleted" rather than collapsing into
          // the "direct mode" case (an erased managed receipt otherwise looks
          // identical: chain_id present, no stored receipt). The entry hash,
          // position, and inclusion proof remain intact.
          <span
            title={`${row.chain_id ?? "(chain redacted)"} — receipt and hosted evidence erased from Sequesign`}
          >
            {row.chain_id ? <code className="muted-code">{truncate(row.chain_id, 18)}</code> : null}
            <span className="badge badge-erased" style={{ marginLeft: row.chain_id ? 6 : 0 }}>
              deleted
            </span>
          </span>
        ) : row.chain_id ? (
          row.receipt_id ? (
            // Managed-mode: the chain produced a stored receipt at
            // Sequesign. The "stored" badge parallels the "direct"
            // badge below so the two custody states read as a single
            // dimension (the receipt envelope is stored, or not).
            <span title={`${row.chain_id} (stored receipt)`}>
              <a
                href={receiptDetailPath(row.receipt_id)}
                className="receipt-link"
                onClick={(e) => e.stopPropagation()}
              >
                <code className="muted-code">{truncate(row.chain_id, 18)}</code>
              </a>
              <span className="badge badge-live" style={{ marginLeft: 6 }}>
                stored
              </span>
            </span>
          ) : (
            <span title={`${row.chain_id} (direct mode)`}>
              <code className="muted-code">{truncate(row.chain_id, 18)}</code>
              <span className="badge badge-test" style={{ marginLeft: 6 }}>
                direct
              </span>
            </span>
          )
        ) : (
          // chain_id NULL with no redaction marker: an account-scope deletion
          // (scrubLogEntriesForCustomer nulls chain_id) or a legacy per-receipt
          // erasure from before the marker existed. Every live chain-extension
          // and satellite entry carries a chain_id and genesis rows are
          // excluded, so a bare null chain_id still means erased.
          <span
            className="badge badge-erased"
            title="Chain linkage erased under data deletion. The entry hash, position, and inclusion proof remain intact; the chain and receipt link were redacted."
          >
            erased
          </span>
        )}
      </td>
      <td>
        <code className="muted-code" title={row.entry_hash}>
          {truncate(row.entry_hash, 12)}
        </code>
      </td>
      <td>
        {row.api_key_prefix ? (
          <code className="muted-code">{row.api_key_prefix}</code>
        ) : (
          <span className="muted">(unknown)</span>
        )}
      </td>
      <td>
        {row.batch_id !== null ? (
          <span className="badge badge-live" title={`Sealed in batch #${row.batch_id}`}>
            sealed
          </span>
        ) : (
          <span className="badge badge-test" title="Pending: not yet sealed">
            pending
          </span>
        )}
      </td>
    </tr>
  );
}

function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
}

function formatTimestamp(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const date = new Date(parsed);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
